import React, { useContext } from "react";
import dayjs from "dayjs";
import Config from "../../appConfig";
import { ConfigContext } from "../../Context/ConfigContext";

const invoiceStyles = {
    invoiceBox: {
        margin: "0px",
        fontSize: "16px",
        lineHeight: "24px",
        fontFamily: "Arial, sans-serif",
        backgroundColor: "#fff",
        boxSizing: "border-box",
        padding: "10px",
        color: "#000",
    },

    table: {
        width: "100%",
        borderCollapse: "collapse",
        fontSize: "12px",
        border: "2px solid #000",
    },

    heading: {
        color: "#000",
    },

    headingCell: {
        fontWeight: "bold",
        border: "1px solid #000",
        textAlign: "center",
        padding: "3px 4px",
    },

    itemCell: {
        padding: "2px 4px",
        verticalAlign: "top",
        borderRight: "1px solid #000",
    },

    total: {
        fontWeight: "bold",
        fontSize: "12px",
    },

    totalLabelCell: {
        padding: "4px 5px",
        textAlign: "right",
        fontWeight: "bold",
    },

    totalValueCell: {
        padding: "4px 5px",
        textAlign: "right",
        fontWeight: "bold",
    },

    shippingAddress: {
        marginBottom: "8px",
        padding: "4px 15px",
        fontSize: "12px",
        borderRadius: "10px",
        border: "2px solid #000",
    },
};

const PrintStyles = () => (
    <style>
        {`
            @page {
                size: A4 portrait;
                margin: 0;
            }

            @media print {
                html,
                body {
                    margin: 0 !important;
                    padding: 0 !important;
                    width: 210mm !important;
                    height: 297mm !important;
                    overflow: hidden !important;
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                }

                body * {
                    visibility: hidden !important;
                }

                .invoice-container,
                .invoice-container * {
                    visibility: visible !important;
                }

                .invoice-container {
                    position: fixed !important;
                    left: 0 !important;
                    top: 0 !important;
                    width: 210mm !important;
                    height: 297mm !important;
                    padding: 6mm !important;
                    margin: 0 !important;
                    box-sizing: border-box !important;
                    background: #fff !important;
                    overflow: hidden !important;
                    display: flex !important;
                    flex-direction: column !important;
                }

                .row {
                    display: flex !important;
                    flex-wrap: wrap !important;
                    margin-left: 0 !important;
                    margin-right: 0 !important;
                }

                .col-7 {
                    width: 58.333333% !important;
                    padding: 0 5px !important;
                    box-sizing: border-box !important;
                }

                .col-5 {
                    width: 41.666667% !important;
                    padding: 0 5px !important;
                    box-sizing: border-box !important;
                }

                .col-8 {
                    width: 66.666667% !important;
                    box-sizing: border-box !important;
                }

                .col-4 {
                    width: 33.333333% !important;
                    box-sizing: border-box !important;
                }

                .col-6 {
                    width: 50% !important;
                    box-sizing: border-box !important;
                }

                table {
                    width: 100% !important;
                    page-break-inside: avoid !important;
                    break-inside: avoid !important;
                }

                tr,
                td,
                th {
                    page-break-inside: avoid !important;
                    break-inside: avoid !important;
                }

                .footer-section {
                    margin-top: auto !important;
                }

                .text-end {
                    text-align: right !important;
                }

                .align-items-center {
                    align-items: center !important;
                }

                .mb-1 {
                    margin-bottom: 4px !important;
                }
            }
        `}
    </style>
);

const ViewInvoice = ({
    order,
    items,
    address,
    returnData,
    cancelData,
    invoice_no,
}) => {
    const dir = localStorage.getItem("dir");

    const { currency } = useContext(ConfigContext);

    const getItemReturns = (itemId) => {
        return (
            returnData?.filter(
                (ret) =>
                    Number(
                        ret.business_return_order_item_id
                    ) === Number(itemId)
            ) || []
        );
    };

    const getTotalReturnedQty = (itemId) => {
        return getItemReturns(itemId).filter((ret) => Number(ret.business_return_status) === 3).reduce(
            (sum, ret) => sum + Number(ret.business_return_qty || 0), 0);
    };

    const getItemCancels = (itemId) => {
        return (
            cancelData?.filter(
                (cancel) =>
                    Number(
                        cancel.business_order_item_id
                    ) === Number(itemId)
            ) || []
        );
    };

    const getTotalCancelledQty = (itemId) => {
        return getItemCancels(itemId)
            .filter(
                (cancel) =>
                    Number(
                        cancel.business_order_cancel_status
                    ) === 1
            )
            .reduce(
                (sum, cancel) =>
                    sum +
                    Number(
                        cancel.business_order_cancel_item_qty ||
                        0
                    ),
                0
            );
    };

    if (!items) {
        return <div></div>;
    }

    return (
        <>
            <PrintStyles />

            <div
                className="invoice-container"
                style={{
                    ...invoiceStyles.invoiceBox,
                    display: "flex",
                    flexDirection: "column",
                }}
                dir={dir}
            >
                {/* =====================================================
                    HEADER
                ====================================================== */}

                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        gap: "20px",
                        paddingBottom: "8px",
                    }}
                >
                    {/* LOGO */}
                    <div
                        style={{
                            width: "150px",
                            flexShrink: 0,
                        }}
                    >
                        <img
                            src={`${Config.country === "uae" ? "/assets/images/Logo/logo_dark.png" : "/assets/images/Logo/logo_2.jpeg"}`}
                            alt="Ghayar"
                            style={{
                                width: "100%",
                                height: "auto",
                                objectFit: "contain",
                                display: "block",
                            }}
                        />
                    </div>

                    {/* COMPANY NAME */}
                    <div
                        style={{
                            flex: 1,
                            textAlign: "center",
                            paddingRight: "20px",
                        }}
                    >
                        <h4
                            style={{
                                margin: 0,
                                fontSize: "18px",
                                fontWeight: "700",
                                color: "#000",
                                lineHeight: "26px",
                                wordBreak: "break-word",
                            }}
                        >
                            {
                                Config.country === "uae" ? "GHAYAR AUTO SPARE PARTS TRADING L.L.C" : "Alemtiyaz Al-Awwal Establishment"
                            }
                        </h4>
                    </div>
                </div>

                {/* =====================================================
                    TAX INVOICE
                ====================================================== */}

                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <span
                        style={{
                            fontSize: "16px",
                            fontWeight: "bold",
                            marginBottom: "8px",
                        }}
                    >
                        Tax Invoice
                    </span>
                </div>

                {/* =====================================================
                    CUSTOMER + INVOICE DETAILS
                ====================================================== */}

                <div
                    className="row"
                    style={{
                        marginBottom: "8px",
                    }}
                >
                    {/* =================================================
                        CUSTOMER DETAILS
                    ================================================== */}

                    <div className="col-7">
                        <div
                            style={{
                                border: "2px solid #000",
                                borderRadius: "10px",
                                padding: "0px 6px",
                                marginBottom: "2px",
                            }}
                        >
                            <span
                                style={{
                                    textTransform: "uppercase",
                                    fontWeight: "bold",
                                    fontSize: "12px",
                                }}
                            >
                                Customer Details :
                            </span>
                        </div>

                        <div
                            style={
                                invoiceStyles.shippingAddress
                            }
                        >
                            <div
                                style={{
                                    display: "flex",
                                    gap: "20px",
                                    flexWrap: "wrap",
                                }}
                            >
                                <div>
                                    <b>Customer Name:</b>{" "}
                                    {order?.business_name}
                                </div>

                                {order?.business_order_ref_client && (
                                    <div>
                                        <b>Ref Client:</b>{" "}
                                        {
                                            order.business_order_ref_client
                                        }
                                    </div>
                                )}
                            </div>

                            <div>
                                <b>Shipping Address:</b>{" "}
                                {address?.business_full_address}
                            </div>

                            <div
                                style={{
                                    display: "flex",
                                    gap: "20px",
                                    flexWrap: "wrap",
                                    marginBottom: "-5px",
                                }}
                            >
                                <div>
                                    <b>City:</b>{" "}
                                    {address?.business_city}
                                </div>

                                <div>
                                    <b>Phone:</b>{" "}
                                    {address?.mobile_number_1}
                                </div>

                                {order?.busienss_trn && (
                                    <div>
                                        <b>TRN:</b>{" "}
                                        {order.busienss_trn}
                                    </div>
                                )}
                            </div>

                            <div>
                                <b>Email:</b>{" "}
                                {address?.contact_email}
                            </div>
                        </div>
                    </div>

                    {/* =================================================
                        INVOICE DETAILS
                    ================================================== */}

                    <div className="col-5">
                        <div
                            style={{
                                border: "2px solid #000",
                                borderRadius: "10px",
                                marginBottom: "2px",
                                textAlign: "center",
                                padding: "2px",
                            }}
                        >
                            <span
                                style={{
                                    textTransform: "uppercase",
                                    fontWeight: "bold",
                                    fontSize: "12px",
                                }}
                            >
                                TRN Number : 100442212500003
                            </span>
                        </div>

                        <div
                            style={
                                invoiceStyles.shippingAddress
                            }
                        >
                            <div>
                                <b>Ref Order No # :</b>{" "}
                                {order?.secret_order_id}
                            </div>

                            <div>
                                <b>Invoice No :</b>{" "}
                                {invoice_no}
                            </div>

                            <div>
                                <b>Date :</b>{" "}
                                {dayjs(
                                    order?.invoice_date
                                ).format("DD MMM YYYY")}
                            </div>

                            <div>
                                <b>Currency :</b>{" "}
                                {currency}
                            </div>

                            <div>
                                <b>Payment Method :</b>{" "}
                                {order?.business_order_payment_method}
                            </div>
                        </div>
                    </div>
                </div>

                {/* =====================================================
                    PRODUCT TABLE
                ====================================================== */}

                <table style={invoiceStyles.table}>
                    <thead>
                        <tr style={invoiceStyles.heading}>
                            <td
                                width="4%"
                                style={
                                    invoiceStyles.headingCell
                                }
                            >
                                Sr No
                            </td>

                            <td
                                style={
                                    invoiceStyles.headingCell
                                }
                            >
                                Product
                            </td>

                            <td
                                style={
                                    invoiceStyles.headingCell
                                }
                                align="center"
                            >
                                Part Number
                            </td>

                            <td
                                style={
                                    invoiceStyles.headingCell
                                }
                                align="center"
                            >
                                Brand Name
                            </td>

                            <td
                                style={
                                    invoiceStyles.headingCell
                                }
                                align="center"
                            >
                                Unit Price
                            </td>

                            <td
                                style={
                                    invoiceStyles.headingCell
                                }
                                align="center"
                            >
                                Quantity
                            </td>

                            <td
                                style={
                                    invoiceStyles.headingCell
                                }
                                align="center"
                            >
                                VAT 5%
                            </td>

                            <td
                                style={
                                    invoiceStyles.headingCell
                                }
                                align="center"
                            >
                                Total
                            </td>
                        </tr>
                    </thead>

                    <tbody>
                        {items.map((item, index) => {
                            const totalReturnedQty = getTotalReturnedQty(item.item_id);
                            const totalCancelledQty = getTotalCancelledQty(item.item_id);

                            const remainingQty = Number(item.item_qty) - Number(totalReturnedQty) - Number(totalCancelledQty);

                            // if (remainingQty <= 0) {
                            //     return null;
                            // }

                            // if (item?.item_status === 6) {
                            //     return null;
                            // }

                            // if (item?.item_status === 7) {
                            //     return null;
                            // }

                            return (
                                <tr
                                    key={item.item_number}
                                >
                                    {/* SR NO */}
                                    <td
                                        style={invoiceStyles.itemCell}
                                        align="center"
                                    >
                                        {index + 1}
                                    </td>

                                    {/* PRODUCT */}
                                    <td
                                        style={
                                            invoiceStyles.itemCell
                                        }
                                    >
                                        {item?.item_name}
                                    </td>

                                    {/* PART NUMBER */}
                                    <td
                                        style={
                                            invoiceStyles.itemCell
                                        }
                                        align="left"
                                    >
                                        {item?.item_number}
                                    </td>

                                    {/* BRAND */}
                                    <td
                                        style={
                                            invoiceStyles.itemCell
                                        }
                                        align="left"
                                    >
                                        {item?.item_brand}
                                    </td>

                                    {/* UNIT PRICE */}
                                    <td
                                        style={
                                            invoiceStyles.itemCell
                                        }
                                        align="right"
                                    >
                                        {item?.item_price}
                                    </td>

                                    {/* QUANTITY */}
                                    <td
                                        style={
                                            invoiceStyles.itemCell
                                        }
                                        align="center"
                                    >
                                        {/* {remainingQty} */}
                                        {new Date() >= new Date("2026-09-01") ? item.item_qty : remainingQty}
                                    </td>

                                    {/* VAT */}
                                    <td
                                        style={
                                            invoiceStyles.itemCell
                                        }
                                        align="right"
                                    >
                                        {item?.item_vat_amount}
                                    </td>

                                    {/* TOTAL */}
                                    <td
                                        style={
                                            invoiceStyles.itemCell
                                        }
                                        align="right"
                                    >
                                        {item?.item_sub_total_excl_vat}
                                    </td>
                                </tr>
                            );
                        })}

                        {/* =================================================
                            SUB TOTAL / TAXABLE AMOUNT
                        ================================================== */}

                        <tr style={invoiceStyles.total}>
                            <td
                                colSpan="7"
                                style={
                                    invoiceStyles.totalLabelCell
                                }
                                align="right"
                            >
                                {Number(items[0]?.accounts_invoice_discount_amount) > 0
                                    ? "Sub Total"
                                    : "Taxable Amount"}{" "}
                                :
                            </td>

                            <td
                                style={
                                    invoiceStyles.totalValueCell
                                }
                                align="right"
                            >
                                {items[0]?.taxable_amount}{" "}
                            </td>
                        </tr>

                        {/* =================================================
                            VAT
                        ================================================== */}

                        {
                            Number(items[0]?.accounts_invoice_discount_amount) > 0 &&
                            <>
                                <tr style={invoiceStyles.total}>
                                    <td
                                        colSpan="7"
                                        style={
                                            invoiceStyles.totalLabelCell
                                        }
                                        align="right"
                                    >
                                        Discount
                                    </td>

                                    <td
                                        style={
                                            invoiceStyles.totalValueCell
                                        }
                                        align="right"
                                    >
                                        <strong>
                                            {items[0]?.accounts_invoice_discount_amount ?? 0}{" "}
                                            {currency}
                                        </strong>
                                    </td>
                                </tr>

                                <tr style={invoiceStyles.total}>
                                    <td
                                        colSpan="7"
                                        style={
                                            invoiceStyles.totalLabelCell
                                        }
                                        align="right"
                                    >
                                        Taxable Amount
                                    </td>

                                    <td
                                        style={
                                            invoiceStyles.totalValueCell
                                        }
                                        align="right"
                                    >
                                        <strong>
                                            {items[0]?.taxable_amount}{" "}
                                        </strong>
                                    </td>
                                </tr>
                            </>
                        }

                        <tr style={invoiceStyles.total}>
                            <td
                                colSpan="7"
                                style={
                                    invoiceStyles.totalLabelCell
                                }
                                align="right"
                            >
                                VAT 5%
                            </td>

                            <td
                                style={invoiceStyles.totalValueCell}
                                align="right"
                            >
                                <strong>
                                    {items[0]?.vat_amount ?? 0}{" "}
                                </strong>
                            </td>
                        </tr>

                        {/* =================================================
                            GRAND TOTAL
                        ================================================== */}

                        <tr style={invoiceStyles.total}>
                            <td
                                colSpan="7"
                                style={invoiceStyles.totalLabelCell}
                                align="right"
                            >
                                Grand Total
                            </td>

                            <td
                                style={invoiceStyles.totalValueCell}
                                align="right"
                            >
                                <strong>
                                    {items[0]?.total_bill_amount}{" "}
                                </strong>
                            </td>
                        </tr>
                    </tbody>
                </table>

                {/* =====================================================
                    FOOTER
                ====================================================== */}

                <div
                    className="footer-section"
                    style={{
                        marginTop: "auto",
                        paddingTop: "8px",
                    }}
                >
                    {/* SIGNATURE */}
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            padding: "8px 16px",
                            alignItems: "center",
                        }}
                    >
                        <div
                            style={{
                                flex: 1,
                            }}
                        >
                            <div
                                style={{
                                    height: "2px",
                                    background: "#000",
                                    width: "100%",
                                }}
                            />

                            <span
                                style={{
                                    textTransform:
                                        "uppercase",
                                    fontSize: "12px",
                                }}
                            >
                                Received by
                            </span>
                        </div>

                        <div
                            style={{
                                flex: 1,
                                marginLeft: "20px",
                            }}
                        >
                            <div
                                style={{
                                    height: "2px",
                                    background: "#000",
                                    width: "100%",
                                }}
                            />

                            <span
                                style={{
                                    textTransform:
                                        "uppercase",
                                    fontSize: "12px",
                                }}
                            >
                                Authorized signatory
                            </span>
                        </div>
                    </div>

                    {/* FOOTER CONTACT */}
                    <div
                        className="row align-items-center"
                        style={{
                            marginTop: "4px",
                        }}
                    >
                        <div className="col-8">
                            <div className="row">
                                {/* WEBSITE */}
                                <div className="col-6 mb-1">
                                    <div
                                        style={{
                                            display: "flex",
                                            gap: "10px",
                                            alignItems:
                                                "center",
                                            fontSize: "11px",
                                        }}
                                    >
                                        <i
                                            className="fa fa-globe"
                                            style={{
                                                color: "red",
                                            }}
                                        />

                                        <span>
                                            www.business.ghayar.com
                                        </span>
                                    </div>
                                </div>

                                {/* EMAIL */}
                                <div className="col-6 mb-1">
                                    <div
                                        style={{
                                            display: "flex",
                                            gap: "10px",
                                            alignItems:
                                                "center",
                                            fontSize: "11px",
                                        }}
                                    >
                                        <i
                                            className="fa fa-envelope"
                                            style={{
                                                color: "red",
                                            }}
                                        />

                                        <span>
                                            business@ghayar.com
                                        </span>
                                    </div>
                                </div>

                                {/* ADDRESS */}
                                <div className="col-6">
                                    <div
                                        style={{
                                            display: "flex",
                                            gap: "10px",
                                            alignItems:
                                                "flex-start",
                                            fontSize: "11px",
                                        }}
                                    >
                                        <i
                                            className="fa fa-map-marker"
                                            style={{
                                                color: "red",
                                                marginTop: "4px",
                                            }}
                                        />

                                        <span>
                                            Algeria Street,
                                            Al Meera Building,
                                            Al Rawda 1,
                                            Ajman
                                        </span>
                                    </div>
                                </div>

                                {/* PHONE */}
                                <div className="col-6">
                                    <div
                                        style={{
                                            display: "flex",
                                            gap: "10px",
                                            alignItems:
                                                "center",
                                            fontSize: "11px",
                                        }}
                                    >
                                        <i
                                            className="fa fa-phone"
                                            style={{
                                                color: "red",
                                            }}
                                        />

                                        <span>
                                            Toll-Free :
                                            800442522
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* UAE IMAGE */}
                        <div className="col-4 d-flex justify-content-end">
                            <img
                                src="/assets/images/Logo/invoice_emirates.png"
                                alt="Emirates"
                                style={{
                                    width: "90px",
                                    objectFit: "contain",
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ViewInvoice;